<script module>
  export const AVAILABLE_LOCATIONS_KEY = 'availableLocation';
</script>

<script>
  import OverpanelControl from '../OverpanelControl/OverpanelControl.svelte';
  import PharmacyLocationAction from '../PharmacyLocationAction/PharmacyLocationAction.svelte';
  import PharmacyLocationActionSkeleton from '../PharmacyLocationActionSkeleton/PharmacyLocationActionSkeleton.svelte';
  import BaseLink from '../BaseLink/BaseLink.svelte';
  import currentLocation from '../../stores/currentLocation';
  import { overpanelMap } from '../../stores/overpanelControl';
  import fetchPharmacies from '../../utils/fetch-pharmacies';
  import { getURL } from '../../utils/card-query';
  
  let {
    title,
    successMessageTemplate,
    failureMessage,
    pendingMessage,
    getCardLinkText,
    lang,
    incomingState = 'pending',
    dataTestId = 'available-locations-overpanel'
  } = $props();

  const initialStates = {
    pending: false,
    success: false,
    failure: false,
  };
  let pharmacies = $state();
  let currentState = $state();

  let drug = $derived(($overpanelMap.get(AVAILABLE_LOCATIONS_KEY) || {}).drug);

  const setState = (state) => {
    currentState = { ...initialStates, [state]: true };
  };

  setState(incomingState);

  const getStateMessage = () => {
    if (currentState.success) return successMessageTemplate.replace(/{{COUNT}}/, pharmacies.length);
    if (currentState.failure) return failureMessage;
    return pendingMessage;
  };

  const appendURL = (res) =>
    res.pharmacies.map((chain) => {
      const options = $overpanelMap.get(AVAILABLE_LOCATIONS_KEY) || {};
      if (options.onClick) {
        return {
          ...chain,
          onClick: () => options.onClick(chain),
        };
      }
      return {
        ...chain,
        url: drug ? getURL({ ...drug, npi: chain.npi, lang }) : `${lang}/savings-card`,
      };
    });

  const sortPharmacies = (pharmacies) => {
    // we want to ensure that all of the pharmacies have a distance value that is parsable.
    // if not, just return the original array unsorted.
    const isDistance = pharmacies.filter((pharm) => parseFloat(pharm.distance)).length === pharmacies.length;

    if (!isDistance) return pharmacies;

    return pharmacies.sort((a, b) => parseFloat(a.distance) - parseFloat(b.distance));
  };

  const mountCallback = () => {
    pharmacies = undefined;

    let lat = null;
    let lng = null;

    if ($currentLocation.city && $currentLocation.street) {
      lat = $currentLocation.center[1];
      lng = $currentLocation.center[0];
    }

    return fetchPharmacies(
      drug.chainCode,
      $currentLocation.zip.toString(),
      lat,
      lng,
      'Error fetching pharmacies in available locations overpanel.'
    )
      .then((response) => response.json())
      .then((data) => {
        pharmacies = sortPharmacies(appendURL(data));
        setState('success');
      })
      .catch(() => {
        setState('failure');
      });
  };

  const closeCallback = () => {
    setState('pending');
  };
</script>

{#if $overpanelMap.has(AVAILABLE_LOCATIONS_KEY) && $currentLocation}
  <OverpanelControl {title} {mountCallback} {closeCallback} overpanelKey={AVAILABLE_LOCATIONS_KEY}>
    <div class="min-h-full bg-gray-mercury" class:justify-between={currentState.failure} data-testid={dataTestId}>
      <div class="flex justify-between p-4 border-b border-gray-opaque-charcoal bg-white">
        <div class="flex-auto pr-4">
          <h2 class="text-sm leading-tight font-medium text-blue-navy">{drug.drug}</h2>
          <span>
            <span class="text-sm leading-tight text-blue-navy">{drug.form.replace(/"/g, '')},</span>
            <span class="text-sm leading-tight text-blue-navy">{drug.strengthLabel},</span>
            <span class="text-sm leading-tight text-blue-navy">{drug.quantityLabel}</span>
          </span>
        </div>
        {#if drug.price}
          <span class="flex-none text-right text-1.5xl font-medium text-blue-cerulean">
            <sup class="text-2xs -top-2">$</sup>
            {drug.price}
          </span>
        {/if}
      </div>
      <div class="px-4 grid gap-4">
        <div
          data-testid="available-locations-overpanel-results-status-message"
          aria-live="assertive"
          aria-label={getStateMessage()}
        ></div>
        {#if currentState.success && pharmacies && pharmacies.length}
          {#each pharmacies as { name, geo: { lat, lng }, address: { street, city, state, zip }, distance, url, onClick }, index}
            <PharmacyLocationAction
              {currentState}
              {name}
              addressLine1={street}
              {city}
              {state}
              {zip}
              {distance}
              {lat}
              {lng}
              {url}
              alt={name}
              {onClick}
              lazyloadImage={index > 3}
            />
          {/each}
        {:else if currentState.pending}
          <!-- eslint-disable @typescript-eslint/no-unused-vars -->
          {#each Array(3) as skeletonTile}
            <PharmacyLocationActionSkeleton />
          {/each}
        {:else}
          <div class="leading-tight text-center text-gray-gunmetal p-8">{failureMessage}</div>
        {/if}
      </div>
      {#if currentState.failure}
        <BaseLink
          className="leading-loose text-center text-black font-medium bg-blue-robin-egg hover-hover:hover:bg-blue-light
          active:bg-blue-malibu focus:outline-none focus:shadow-focus active:shadow-active sm:rounded-md whitespace-nowrap 
          min-w-25 px-8 flex items-center justify-center h-16 sm:h-12 mt-0.5 mx-auto sm:max-w-sm w-full sm:my-4"
          url={drug ? getURL({ ...drug, lang }) : `${lang}/savings-card`}
        >
          {getCardLinkText}
        </BaseLink>
      {/if}
    </div>
  </OverpanelControl>
{/if}
